/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 */

import { createContext, useCallback, useContext, useRef, useState } from "react";

// In-brand replacement for window.confirm. Usage:
//   const confirm = useConfirm();
//   if (await confirm({ title, message, confirmLabel, danger: true })) { ... }
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({
        title: opts.title || "Are you sure?",
        message: opts.message || "",
        confirmLabel: opts.confirmLabel || "Confirm",
        cancelLabel: opts.cancelLabel || "Cancel",
        danger: !!opts.danger,
      });
    });
  }, []);

  const close = useCallback((result) => {
    setState(null);
    if (resolver.current) { resolver.current(result); resolver.current = null; }
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div className="modal-overlay" onClick={() => close(false)} role="presentation">
          <div
            className="modal-card"
            role="alertdialog"
            aria-modal="true"
            aria-label={state.title}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="modal-title">{state.title}</h2>
            {state.message && <p className="modal-message">{state.message}</p>}
            <div className="modal-actions">
              <button className="secondary" onClick={() => close(false)}>{state.cancelLabel}</button>
              <button className={state.danger ? "danger" : ""} onClick={() => close(true)} autoFocus>
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside ConfirmProvider");
  return ctx;
}
